import { Col, Row } from 'antd';
import { Pin } from 'types';
import { formatToDate } from 'utils/formatToDate';

import { DocumentDetailsModel } from 'api/documents/documents.model';
import { BasicCard } from 'components/BasicCard';
import CardRow from 'components/CardRow';
import CompositionRow from 'components/CompositionRow';
import Detail from 'components/Detail';
import Map from 'components/Map';
import { extractCoordinatesFromProduct } from 'components/Map/utils';
import SectionTitle from 'components/SectionTitle';
import SubTitle from 'components/SubTitle';
import useCertificateHelper from 'hooks/useCertificateHelper';
import {
  ADDITIONAL_DETAILS,
  CertificateDetailsModel,
  CHEMICAL_COMPOSITION_DETAILS,
  PARTICLE_SIZE_DISTRIBUTION_DETAILS,
  PHYSICAL_PROPERTIES_DETAILS,
  PRODUCER_DETAILS,
} from 'models/certificate.model';
import { COLOR } from 'styles/styledComponents/color.model';

import NotProvidedValueCard from '../components/NotProvidedValueCard';
import ScoreCard from '../components/ScoreCard';
import ScoreDetail from '../components/ScoreDetail';
import Template, { ScoreContainer } from '../components/Template';

const Chemistry = ({
  additionalDetails = {} as CertificateDetailsModel,
  ...document
}: DocumentDetailsModel<CertificateDetailsModel>) => {
  const { getLabel, getValueByLabel } = useCertificateHelper();

  const pins: Pin[] = [];

  if (additionalDetails[ADDITIONAL_DETAILS.LITHIUM_PRODUCER]) {
    const coordinates = extractCoordinatesFromProduct(
      additionalDetails[ADDITIONAL_DETAILS.LITHIUM_PRODUCER]
    );

    if (coordinates) {
      pins.push({
        coordinates,
        iconPath: '/pin-blue.svg',
      });
    }
  }

  if (additionalDetails[ADDITIONAL_DETAILS.COBALT_PRODUCER]) {
    const coordinates = extractCoordinatesFromProduct(
      additionalDetails[ADDITIONAL_DETAILS.COBALT_PRODUCER]
    );

    if (coordinates) {
      pins.push({
        coordinates,
        iconPath: '/pin-orange.svg',
      });
    }
  }

  return (
    <Template
      type={document.category}
      jobId={document.jobId}
      favourite={document.favourite}
      batchCard={
        <>
          <Detail
            label={getLabel('batchNumber(CertificateUID)')}
            value={additionalDetails.productId}
          />
          <Detail label={getLabel('passportId')} value={document.documentBlockchainAddress} />
          <CardRow>
            <Detail
              label={getLabel(ADDITIONAL_DETAILS.ASSEMBLY_NUMBER)}
              value={additionalDetails[ADDITIONAL_DETAILS.ASSEMBLY_NUMBER]}
            />
            <Detail
              label={getLabel(ADDITIONAL_DETAILS.CONTROL_NUMBER)}
              value={additionalDetails[ADDITIONAL_DETAILS.CONTROL_NUMBER]}
            />
          </CardRow>
        </>
      }
      detailsCard={
        <CardRow>
          <Detail label={getLabel('fullName')} value={document.title} />
          <Detail
            label={getLabel(ADDITIONAL_DETAILS.CHEMISTRY_MARKET_NAME)}
            value={additionalDetails[ADDITIONAL_DETAILS.CHEMISTRY_MARKET_NAME]}
          />
          <Detail
            label={getLabel(ADDITIONAL_DETAILS.PRODUCTION_DATE)}
            value={formatToDate(additionalDetails[ADDITIONAL_DETAILS.PRODUCTION_DATE])}
          />
          <Detail
            label={getLabel(ADDITIONAL_DETAILS.SOURCED)}
            value={additionalDetails[ADDITIONAL_DETAILS.SOURCED]}
          />
          <Detail
            label={getLabel(ADDITIONAL_DETAILS.CHEMICAL_NAME_OR_MATERIAL)}
            value={additionalDetails[ADDITIONAL_DETAILS.CHEMICAL_NAME_OR_MATERIAL]}
          />
          <Detail
            label={getLabel(ADDITIONAL_DETAILS.MOLECULAR_FORMULA)}
            value={additionalDetails[ADDITIONAL_DETAILS.MOLECULAR_FORMULA]}
          />
        </CardRow>
      }
    >
      <SectionTitle>{getLabel('scoring')}</SectionTitle>
      <BasicCard $justify="center">
        <ScoreContainer>
          <ScoreCard label={getLabel(ADDITIONAL_DETAILS.LITHIUM_PRODUCER)}>
            <ScoreDetail
              label={getLabel(PRODUCER_DETAILS.CHILD_LABOR_SCORING)}
              maxScore={6}
              score={getValueByLabel(
                PRODUCER_DETAILS.CHILD_LABOR_SCORING,
                additionalDetails[ADDITIONAL_DETAILS.LITHIUM_PRODUCER]
              )}
            />
            <ScoreDetail
              label={getLabel(PRODUCER_DETAILS.HUMAN_RIGHTS_SCORING)}
              maxScore={6}
              score={getValueByLabel(
                PRODUCER_DETAILS.HUMAN_RIGHTS_SCORING,
                additionalDetails[ADDITIONAL_DETAILS.LITHIUM_PRODUCER]
              )}
            />
          </ScoreCard>
          <ScoreCard label={getLabel(ADDITIONAL_DETAILS.COBALT_PRODUCER)}>
            <ScoreDetail
              label={getLabel(PRODUCER_DETAILS.CHILD_LABOR_SCORING)}
              maxScore={6}
              score={getValueByLabel(
                PRODUCER_DETAILS.CHILD_LABOR_SCORING,
                additionalDetails[ADDITIONAL_DETAILS.COBALT_PRODUCER]
              )}
            />
            <ScoreDetail
              label={getLabel(PRODUCER_DETAILS.HUMAN_RIGHTS_SCORING)}
              maxScore={6}
              score={getValueByLabel(
                PRODUCER_DETAILS.HUMAN_RIGHTS_SCORING,
                additionalDetails[ADDITIONAL_DETAILS.COBALT_PRODUCER]
              )}
            />
          </ScoreCard>
        </ScoreContainer>
      </BasicCard>

      <SectionTitle>{getLabel('specification')}</SectionTitle>
      <Row gutter={[16, 16]}>
        <Col md={24} lg={12}>
          <SubTitle>{getLabel(ADDITIONAL_DETAILS.CHEMICAL_COMPOSITION)}</SubTitle>
          <CompositionRow<CHEMICAL_COMPOSITION_DETAILS>
            composition={additionalDetails?.[ADDITIONAL_DETAILS.CHEMICAL_COMPOSITION]}
            labels={Object.values(CHEMICAL_COMPOSITION_DETAILS)}
          />
        </Col>
        <Col md={24} lg={12}>
          <SubTitle>{getLabel(ADDITIONAL_DETAILS.PHYSICAL_PROPERTIES)}</SubTitle>
          <CompositionRow
            composition={additionalDetails[ADDITIONAL_DETAILS.PHYSICAL_PROPERTIES]}
            labels={Object.values(PHYSICAL_PROPERTIES_DETAILS)}
          />

          <SubTitle>{getLabel(ADDITIONAL_DETAILS.PARTICLE_SIZE_DISTRIBUTION)}</SubTitle>
          <CompositionRow
            composition={additionalDetails[ADDITIONAL_DETAILS.PARTICLE_SIZE_DISTRIBUTION]}
            labels={Object.values(PARTICLE_SIZE_DISTRIBUTION_DETAILS)}
          />

          <SubTitle>{getLabel(ADDITIONAL_DETAILS.ELECTROCHEMICAL_PERFORMANCE)}</SubTitle>
          {additionalDetails[ADDITIONAL_DETAILS.ELECTROCHEMICAL_PERFORMANCE] ? (
            <BasicCard>
              {additionalDetails[ADDITIONAL_DETAILS.ELECTROCHEMICAL_PERFORMANCE]}
            </BasicCard>
          ) : (
            <NotProvidedValueCard />
          )}
        </Col>
      </Row>

      <SectionTitle>{getLabel('chemistryProducers')}</SectionTitle>
      <Map
        pins={pins}
        card={
          <BasicCard>
            <Row gutter={[80, 16]}>
              <Col md={24} lg={12}>
                <SectionTitle noMargin dividerMargin="1rem" color={COLOR.BATTERY_PACKS}>
                  {getLabel(ADDITIONAL_DETAILS.COBALT_PRODUCER)}
                </SectionTitle>
                <Detail
                  label={getLabel(PRODUCER_DETAILS.COMPANY)}
                  value={getValueByLabel(
                    PRODUCER_DETAILS.COMPANY,
                    additionalDetails[ADDITIONAL_DETAILS.COBALT_PRODUCER]
                  )}
                />
                <Detail
                  label={getLabel(PRODUCER_DETAILS.COMPANY_HEADQUATERS)}
                  value={getValueByLabel(
                    PRODUCER_DETAILS.COMPANY_HEADQUATERS,
                    additionalDetails[ADDITIONAL_DETAILS.COBALT_PRODUCER]
                  )}
                />
                <Detail
                  label={getLabel(PRODUCER_DETAILS.MINE)}
                  value={getValueByLabel(
                    PRODUCER_DETAILS.MINE,
                    additionalDetails[ADDITIONAL_DETAILS.COBALT_PRODUCER]
                  )}
                />
                <Detail
                  label={getLabel(PRODUCER_DETAILS.COORDINATES)}
                  value={getValueByLabel(
                    PRODUCER_DETAILS.COORDINATES,
                    additionalDetails[ADDITIONAL_DETAILS.COBALT_PRODUCER]
                  )}
                />
              </Col>
              <Col md={24} lg={12}>
                <SectionTitle noMargin dividerMargin="1rem">
                  {getLabel(ADDITIONAL_DETAILS.LITHIUM_PRODUCER)}
                </SectionTitle>
                <Detail
                  label={getLabel(PRODUCER_DETAILS.COMPANY)}
                  value={getValueByLabel(
                    PRODUCER_DETAILS.COMPANY,
                    additionalDetails.lithiumProducer
                  )}
                />
                <Detail
                  label={getLabel(PRODUCER_DETAILS.COMPANY_HEADQUATERS)}
                  value={getValueByLabel(
                    PRODUCER_DETAILS.COMPANY_HEADQUATERS,
                    additionalDetails.lithiumProducer
                  )}
                />
                <Detail
                  label={getLabel(PRODUCER_DETAILS.MINE)}
                  value={getValueByLabel(PRODUCER_DETAILS.MINE, additionalDetails.lithiumProducer)}
                />
                <Detail
                  label={getLabel(PRODUCER_DETAILS.COORDINATES)}
                  value={getValueByLabel(
                    PRODUCER_DETAILS.COORDINATES,
                    additionalDetails.lithiumProducer
                  )}
                />
              </Col>
            </Row>
          </BasicCard>
        }
      />
    </Template>
  );
};

export default Chemistry;
